import { Check, RotateCw, Send, TriangleAlert } from "lucide-react";
import { Component, useEffect, useRef, useState, type ErrorInfo, type KeyboardEvent, type ReactNode } from "react";
import { dismissCrash, errorLike, prepareReport, sendReport, stopTheApp, useCrash, type CrashInfo } from "../lib/crash.ts";
import { MAX_NOTE, type CrashReport } from "../lib/crashReport.ts";
import { AcademyLink } from "./Academy.tsx";
import { LegalLink } from "./Legal.tsx";

type SendState = { state: "idle" | "sending" | "sent" } | { state: "error"; message: string };

/**
 * The crash screen: what happened, the whole report it would send (redacted, lib/crashReport.ts), a note, and
 * "Send to Lab43" — nothing is sent without that click, and a failed send is only tried again by pressing Send again.
 * `over`: shown over the still-running app (an uncaught error that keeps coming), with Close: a modal dialog then
 * (Escape closes it, the keyboard stays inside, the app's shortcuts don't act behind it, the focus goes back where it
 * was), and not a second <main>.
 */
export function CrashScreen({ info, over = false }: { info: CrashInfo; over?: boolean }) {
  const [report, setReport] = useState<CrashReport | null>(null);
  const [note, setNote] = useState("");
  const [send, setSend] = useState<SendState>({ state: "idle" });
  const heading = useRef<HTMLHeadingElement>(null);
  const box = useRef<HTMLElement>(null);

  // over the app: the focus goes back where it was once the screen closes (noted before the heading takes it); while
  // it's open, keys and copy, cut and paste never reach the app's own listeners (controller.ts listens on window: L, 6,
  // 7, Ctrl+S, the tools, and copying or cutting a plan object), even when the focus has fallen outside the dialog
  useEffect(() => {
    if (!over) return;
    const before = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const inside = (t: EventTarget | null) => t instanceof Node && !!box.current?.contains(t);
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (inside(e.target)) return; // the dialog's own onKeyDown handles it and stops it there
      e.stopPropagation();
      if (e.key === "Escape") { e.preventDefault(); dismissCrash(); }
      else if (e.key === "Tab") { e.preventDefault(); heading.current?.focus(); }
    };
    const onClip = (e: Event) => e.stopPropagation(); // the browser still copies the selected report text
    window.addEventListener("keydown", onKey, true);
    for (const t of ["copy", "cut", "paste"]) window.addEventListener(t, onClip, true);
    return () => {
      window.removeEventListener("keydown", onKey, true);
      for (const t of ["copy", "cut", "paste"]) window.removeEventListener(t, onClip, true);
      if (before?.isConnected) before.focus();
    };
  }, [over]);

  useEffect(() => {
    let live = true;
    void prepareReport(info).then((r) => { if (live) setReport(r); });
    heading.current?.focus();
    return () => { live = false; };
  }, [info]);

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!over) return;
    e.stopPropagation(); // the app's own shortcuts (controller.ts) listen on window: none acts behind the dialog
    if (e.key === "Escape") { e.preventDefault(); dismissCrash(); return; }
    if (e.key !== "Tab") return;
    const items = [...(box.current?.querySelectorAll<HTMLElement>("button:not([disabled]), textarea, a[href], [tabindex='0']") ?? [])];
    if (!items.length) return;
    const first = items[0], last = items[items.length - 1];
    const at = document.activeElement;
    if (e.shiftKey && (at === first || at === heading.current || !box.current?.contains(at))) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && (at === last || !box.current?.contains(at))) { e.preventDefault(); first.focus(); }
  };

  const onSend = async () => {
    if (!report || send.state === "sending" || send.state === "sent") return;
    setSend({ state: "sending" });
    const res = await sendReport(report, note);
    setSend(res.ok ? { state: "sent" } : { state: "error", message: res.message });
  };

  const sent = send.state === "sent";
  // replacing the app it is the page's main content; over the app (which has its own <main>) a dialog
  const Box = over ? "section" : "main";
  return (
    <div
      className={`crash-back${over ? " over" : ""}`}
      onKeyDown={onKeyDown}
      // over the app, a click outside the buttons and fields keeps the focus in the dialog
      onMouseDown={over ? (e) => { if (!(e.target as HTMLElement).closest("button, textarea, a, pre")) { e.preventDefault(); heading.current?.focus(); } } : undefined}
    >
      <Box ref={box} className="crash" aria-labelledby="crash-title" role={over ? "alertdialog" : undefined} aria-modal={over || undefined} tabIndex={over ? -1 : undefined}>
        <header className="crash-head">
          <span className="crash-badge" aria-hidden="true"><TriangleAlert size={22} /></span>
          <div>
            <h1 id="crash-title" ref={heading} tabIndex={-1}>{over ? "MC Planner keeps hitting an error" : "MC Planner crashed"}</h1>
            <p className="small muted">Your plans are saved as you go. Reload to carry on.</p>
          </div>
        </header>

        <h2 id="crash-send-title">Send a crash report to Lab43?</h2>
        <p className="small">This is the whole report. Nothing is sent unless you press Send.</p>
        <label className="crash-note">
          <span>What were you doing? <span className="muted">(optional)</span></span>
          <textarea
            value={note}
            maxLength={MAX_NOTE}
            rows={2}
            readOnly={sent}
            placeholder="e.g. I dropped a schematic on the map"
            onChange={(e) => setNote(e.target.value)}
          />
        </label>
        <pre className="crash-report" tabIndex={0} aria-label="The crash report" aria-busy={!report}>
          {report ? report.report : "Preparing the report…"}
        </pre>

        <div className="row crash-actions">
          {/* aria-disabled while sending and once sent, so the button keeps the keyboard focus */}
          <button className="primary" disabled={!report} aria-disabled={send.state === "sending" || sent || undefined} onClick={() => void onSend()}>
            {sent ? <><Check className="lead" size={16} aria-hidden="true" />Sent</>
              : <><Send className="lead" size={16} aria-hidden="true" />{send.state === "sending" ? "Sending…" : "Send to Lab43"}</>}
          </button>
          <button onClick={() => location.reload()}><RotateCw className="lead" size={16} aria-hidden="true" />Reload MC Planner</button>
          {over && <button className="ghost" onClick={dismissCrash}>Close</button>}
        </div>
        <p className={`crash-status small${sent ? " ok" : send.state === "error" ? " err" : ""}`} role="status">
          {sent ? "Sent, thank you." : send.state === "error" ? send.message : ""}
        </p>
        <p className="tiny muted crash-privacy">
          Sent to auth.lab43.dev, Lab43's account site, and kept for 90 days. Never in it: your plans, world, file or
          schematic names, invite links, your email or account.{" "}
          <LegalLink path="legal/privacy.html#crash-reports">Privacy</LegalLink>
          {" · "}
          <AcademyLink to="crashReport" about="crash reports" />
        </p>
      </Box>
    </div>
  );
}

/** The crash screen over the app for an uncaught error that keeps coming (lib/crash.ts). */
function RepeatedErrorScreen() {
  const info = useCrash((s) => s.info);
  return info?.where === "repeated" ? <CrashScreen info={info} over /> : null;
}

/** If even the crash screen fails: plain words and a way back. */
class LastResort extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div className="crash-back">
        <main className="crash" aria-labelledby="crash-title">
          <h1 id="crash-title">MC Planner crashed</h1>
          <p className="small">Your plans are saved as you go.</p>
          <div className="row crash-actions"><button className="primary" onClick={() => location.reload()}>Reload MC Planner</button></div>
        </main>
      </div>
    );
  }
}

/**
 * Around the whole app (main.tsx): an error while React draws it, or in one of its effects (the 3D view failing to
 * start, for example), replaces the app with the crash screen.
 */
export class CrashBoundary extends Component<{ children: ReactNode }, { info: CrashInfo | null }> {
  state: { info: CrashInfo | null } = { info: null };

  static getDerivedStateFromError(error: unknown) {
    return { info: { where: "react", error: errorLike(error) } satisfies CrashInfo };
  }

  componentDidCatch(error: unknown, errorInfo: ErrorInfo) {
    stopTheApp();
    this.setState({ info: { where: "react", error: errorLike(error), componentStack: errorInfo.componentStack ?? undefined } });
  }

  render() {
    if (this.state.info) return <LastResort><CrashScreen info={this.state.info} /></LastResort>;
    return (
      <>
        {this.props.children}
        <LastResort><RepeatedErrorScreen /></LastResort>
      </>
    );
  }
}
